import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import type { Pointage, PointageKind, Vacation } from "@victor/api-client";
import { ApiClientError } from "@victor/api-client";
import { api } from "../../lib/api";
import {
  tryGetCurrentPosition,
  type GeolocationFailureReason,
} from "../../lib/geolocation";
import { enqueueOrSend } from "../../lib/sync-manager";

/**
 * Vague 1 · Phase C.2 · Bloc pointage intégré dans la VacationCard.
 *
 * Affiche les boutons « Prendre mon poste » / « Quitter mon poste » selon
 * l'état des pointages existants. Capture la geoloc avant l'envoi.
 * Si la geoloc est refusée, le pointage part quand même (flagué côté serveur).
 */
export function PointageBlock({ vacation }: { vacation: Vacation }) {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<
    | { kind: "success"; horsCreneau: boolean; ecartMetres: number | null }
    | { kind: "queued" }
    | { kind: "error"; message: string }
    | null
  >(null);
  const [geoWarn, setGeoWarn] = useState<GeolocationFailureReason | null>(null);

  const pointages = useQuery({
    queryKey: ["me-pointages", vacation.id],
    queryFn: () => api.me.pointages(vacation.id),
    enabled: vacation.statut !== "annulee",
  });

  const prise = pointages.data?.find((p) => p.kind === "prise_poste") ?? null;
  const sortie = pointages.data?.find((p) => p.kind === "sortie_poste") ?? null;

  const mutate = useMutation({
    mutationFn: async (kind: PointageKind) => {
      setFeedback(null);
      setGeoWarn(null);
      const { position, reason } = await tryGetCurrentPosition();
      if (reason) setGeoWarn(reason);
      const payload = {
        vacationId: vacation.id,
        kind,
        latitude: position?.latitude ?? null,
        longitude: position?.longitude ?? null,
        precisionMetres: position?.precisionMetres ?? null,
        pointeeClientAt: new Date().toISOString(),
      };
      return enqueueOrSend(
        "pointage",
        payload,
        { vacationId: vacation.id },
        () => api.me.pointage(payload),
      );
    },
    onSuccess: (res) => {
      if (res.kind === "sent") {
        setFeedback({
          kind: "success",
          horsCreneau: res.result.horsCreneau,
          ecartMetres: res.result.ecartMetres,
        });
        qc.invalidateQueries({ queryKey: ["me-pointages", vacation.id] });
      } else {
        // Réseau down · l'op est en outbox, le scheduler la rejouera.
        setFeedback({ kind: "queued" });
      }
    },
    onError: (err: unknown) => {
      const message = formatPointageError(err);
      setFeedback({ kind: "error", message });
    },
  });

  if (vacation.statut === "annulee") return null;

  // Tout pointé → résumé compact (pas de bouton)
  if (prise && sortie) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-xl bg-green-50 px-3 py-2 text-xs font-semibold text-green-800">
        <CheckIcon />
        <span>
          Pointé · {formatHour(prise.pointeeAt)} → {formatHour(sortie.pointeeAt)}
        </span>
      </div>
    );
  }

  // Étape suivante : prise (si pas encore) sinon sortie
  const nextKind: PointageKind = prise ? "sortie_poste" : "prise_poste";
  const label = prise ? "Quitter mon poste" : "Prendre mon poste";

  return (
    <div className="mt-3 space-y-2">
      {prise ? (
        <p className="text-xs font-semibold text-navy-500">
          Prise de poste · {formatHour(prise.pointeeAt)}
        </p>
      ) : null}

      <button
        type="button"
        disabled={mutate.isPending}
        onClick={() => mutate.mutate(nextKind)}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-500 px-4 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-accent-600 disabled:opacity-60"
      >
        {mutate.isPending ? (
          <Spinner />
        ) : (
          <>
            <PinIcon />
            {label}
          </>
        )}
      </button>

      <AnimatePresence>
        {feedback ? (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`rounded-xl px-3 py-2 text-xs font-semibold ${
              feedback.kind === "success"
                ? "bg-green-50 text-green-800"
                : feedback.kind === "queued"
                  ? "bg-amber-50 text-amber-800"
                  : "bg-red-50 text-red-800"
            }`}
          >
            {feedback.kind === "success" ? (
              <FeedbackSuccess
                horsCreneau={feedback.horsCreneau}
                ecartMetres={feedback.ecartMetres}
                geoWarn={geoWarn}
              />
            ) : feedback.kind === "queued" ? (
              <span>
                Hors réseau · pointage enregistré localement, synchronisation
                automatique au retour du signal.
              </span>
            ) : (
              <span>{feedback.message}</span>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FeedbackSuccess({
  horsCreneau,
  ecartMetres,
  geoWarn,
}: {
  horsCreneau: boolean;
  ecartMetres: number | null;
  geoWarn: GeolocationFailureReason | null;
}) {
  const parts: string[] = [];
  if (geoWarn === "denied") {
    parts.push("Localisation refusée · pointage enregistré sans coordonnées.");
  } else if (geoWarn) {
    parts.push("Localisation indisponible · pointage enregistré quand même.");
  }
  if (ecartMetres != null) parts.push(`À ${ecartMetres} m du site.`);
  if (horsCreneau) parts.push("Hors créneau planifié.");
  return <span>{parts.length > 0 ? parts.join(" ") : "Pointage enregistré."}</span>;
}

function formatPointageError(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case "pointage_already_exists":
        return "Vous avez déjà pointé.";
      case "missing_prise_poste":
        return "Pointez d'abord votre prise de poste.";
      case "not_your_vacation":
        return "Cette vacation ne vous est pas affectée.";
      case "vacation_annulee":
        return "Vacation annulée, pointage impossible.";
      case "agent_not_found":
        return "Votre compte n'est pas relié à un agent terrain.";
      case "forbidden":
        return "Vous n'avez pas la permission de pointer.";
      default:
        return err.message || "Échec du pointage.";
    }
  }
  return "Échec du pointage. Vérifiez votre connexion.";
}

function formatHour(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------

function PinIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="9" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" />
    </svg>
  );
}

// Réexport au cas où · préfixé pour éviter collision avec d'éventuels
// composants `Pointage` de la même app.
export type { Pointage };
